// "Separar en capas" en el panel de herramientas del visor
// (ADR-2026-10-07-separar-en-capas-en-el-visor).
//
// Antes de separar: la ilustración, y un interruptor visible para escribir QUÉ separar; lo
// escrito va tal cual a Seedream y, mientras se escribe, Gemini Flash-Lite revisa que sea
// eso y si no, un cuadrito recuerda para qué es el campo (no bloquea). Sin el interruptor,
// el backend pide los textos y gráficos principales (o, si no hay, las personas y los
// objetos grandes). Cuesta 1 crédito.
//
// Al terminar, las capas abren en 3D sobre la imagen un instante y vuelven a su lugar.
// Desde ahí: en la imagen se arrastran, se escalan desde las esquinas y se giran desde el
// punto de arriba; en el panel se reordenan arrastrando y se ocultan con el ojo. "Guardar
// versión" compone lo que se ve y lo guarda como versión nueva (gratis); "Cancelar"
// descarta, con confirmación porque el crédito ya se usó.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as PE, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Bookmark, BookmarkCheck, Eye, EyeOff, GripVertical, Info, Loader2, Lock, RotateCcw, Sparkles } from 'lucide-react';
import { BotonConCreditos } from './BotonConCreditos';
import { IlustracionDeCapas } from './IlustracionDeCapas';
import { Cuerpo, Pie, Seccion, useCalceSobre } from './piezasDelPanel';
import {
  CREDITOS_EDITAR_CAPA,
  CREDITOS_SEPARAR_EN_CAPAS,
  MAX_LARGO_INSTRUCCION_CAPAS,
  escalarCapa,
  moverCapa,
  pasoDeHistorial,
  revertirEdicion,
  reordenarCapas,
  revisarInstruccionDeCapas,
  rotacionConIman,
  sinCambios,
  type CapaEnLienzo,
  type EdicionDeCapa,
  type Separacion,
} from '../../lib/separarEnCapas';
import { formatoDeMedidas } from '../../lib/versionesDelVisor';

const CURVA = 'cubic-bezier(0.32,0.72,0,1)';
/** Cuánto se separa cada capa de la siguiente en la vista abierta, en px. */
const PASO_Z = 46;
/** La apertura al terminar de separar: cuánto tarda en abrirse/cerrarse y cuánto queda abierta. */
const ABRIR_MS = 650;
const ABIERTA_MS = 750;
/** Pausa del texto antes de revisarlo con Flash-Lite. */
const ESPERA_REVISION_MS = 800;
/** Alto de una fila de la lista + su separación (para el reordenamiento). */
const ALTO_FILA = 52;
const PASO_FILA = ALTO_FILA + 4;

const prefiereMenosMovimiento = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// ── El lienzo sobre la imagen ───────────────────────────────────────────────────

type Gesto =
  | { tipo: 'mover'; id: string; x0: number; y0: number; capa: CapaEnLienzo }
  | { tipo: 'escalar'; id: string; d0: number; cx: number; cy: number; capa: CapaEnLienzo }
  | { tipo: 'rotar'; id: string; a0: number; cx: number; cy: number; capa: CapaEnLienzo };

const TABLERO: CSSProperties = {
  backgroundColor: 'rgba(255,255,255,0.9)',
  backgroundImage:
    'linear-gradient(45deg,#d9dce4 25%,transparent 25%),linear-gradient(-45deg,#d9dce4 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#d9dce4 75%),linear-gradient(-45deg,transparent 75%,#d9dce4 75%)',
  backgroundSize: '10px 10px',
  backgroundPosition: '0 0,0 5px,5px -5px,-5px 0',
};

function cajaCss(c: CapaEnLienzo): CSSProperties {
  return {
    left: `${(c.cx - c.w / 2) * 100}%`,
    top: `${(c.cy - c.h / 2) * 100}%`,
    width: `${c.w * 100}%`,
    height: `${c.h * 100}%`,
  };
}

const LienzoDeCapas: React.FC<{
  rect: { left: number; top: number; width: number; height: number };
  capas: CapaEnLienzo[];
  /** 0 = apiladas en su lugar, 1 = abiertas en 3D. */
  abiertas: boolean;
  /**
   * El lienzo está en 3D (sólo mientras dura la apertura). En un contexto `preserve-3d` el
   * navegador IGNORA el `z-index` y ordena por profundidad; con todas las capas a la misma
   * profundidad, el orden queda a su criterio (Safari las mezcla). Fuera de la animación el
   * lienzo es plano y manda el `z-index` (ADR-2026-10-07-las-capas-se-apilan-en-plano).
   */
  en3D: boolean;
  interactivo: boolean;
  ocupado: boolean;
  seleccion: string | null;
  resaltada: string | null;
  onSeleccionar: (id: string | null) => void;
  onCambiar: (c: CapaEnLienzo) => void;
  /** La capa que se está editando con IA: el brillo pasa sólo sobre ella. */
  capaEnProceso: string | null;
  /** Lo que va debajo de la capa elegida (el botón "Editar capa" y su barra). */
  bajoLaCapa?: (c: CapaEnLienzo) => ReactNode;
}> = ({ rect, capas, abiertas, en3D, interactivo, ocupado, seleccion, resaltada, onSeleccionar, onCambiar, capaEnProceso, bajoLaCapa }) => {
  const gesto = useRef<Gesto | null>(null);
  // Mientras se arrastra, escala o gira, el botón de debajo se esconde (taparía el gesto).
  const [moviendo, setMoviendo] = useState(false);
  const n = capas.length;

  const empezar = (e: PE<HTMLElement>, g: Gesto) => {
    e.stopPropagation();
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    gesto.current = g;
    setMoviendo(true);
  };
  const seguir = (e: PE<HTMLElement>) => {
    const g = gesto.current;
    if (!g) return;
    if (g.tipo === 'mover') {
      onCambiar(moverCapa(g.capa, (e.clientX - g.x0) / rect.width, (e.clientY - g.y0) / rect.height));
    } else if (g.tipo === 'escalar') {
      const d = Math.hypot(e.clientX - g.cx, e.clientY - g.cy);
      onCambiar(escalarCapa(g.capa, g.d0 > 0 ? d / g.d0 : 1));
    } else {
      const a = (Math.atan2(e.clientY - g.cy, e.clientX - g.cx) * 180) / Math.PI;
      onCambiar({ ...g.capa, rot: rotacionConIman(g.capa.rot + a - g.a0) });
    }
  };
  const soltar = () => {
    gesto.current = null;
    setMoviendo(false);
  };
  /** El centro de la capa en pantalla (para escalar y rotar sobre él). */
  const centro = (c: CapaEnLienzo) => ({ cx: rect.left + c.cx * rect.width, cy: rect.top + c.cy * rect.height });

  const elegida = capas.find((c) => c.id === seleccion && !c.esBase && c.visible) ?? null;
  /** Debajo de la caja GIRADA de la elegida (su envolvente), centrado; sin salirse de la ventana. */
  const anclaDebajo = (c: CapaEnLienzo) => {
    const w = c.w * rect.width;
    const h = c.h * rect.height;
    const t = (c.rot * Math.PI) / 180;
    const mitadAlto = (Math.abs(w * Math.sin(t)) + Math.abs(h * Math.cos(t))) / 2;
    const top = Math.min(c.cy * rect.height + mitadAlto + 14, window.innerHeight - 16 - 120 - rect.top);
    const left = Math.min(Math.max(c.cx * rect.width, 216 - rect.left), window.innerWidth - 404 - 216 - rect.left);
    return { top, left };
  };
  const transicion = gesto.current ? 'none' : `transform ${ABRIR_MS}ms ${CURVA}, opacity 220ms ease-out`;

  return createPortal(
    <div
      className="fixed z-[55] animate-fade-in"
      style={{ ...rect, perspective: 1600, pointerEvents: interactivo ? 'auto' : 'none' }}
      onPointerDown={() => interactivo && onSeleccionar(null)}
    >
      <div
        className="relative h-full w-full"
        style={{
          transformStyle: en3D ? 'preserve-3d' : 'flat',
          transform: abiertas ? 'rotateX(52deg) rotateZ(-30deg) scale(0.78)' : 'none',
          transition: `transform ${ABRIR_MS}ms ${CURVA}, opacity 300ms ease-out`,
          opacity: ocupado ? 0.85 : 1,
        }}
      >
        {capas.map((c, i) => (
          <div
            key={c.id}
            className={`absolute select-none ${c.esBase ? 'overflow-hidden rounded-lg' : ''}`}
            style={{
              ...cajaCss(c),
              zIndex: i,
              opacity: c.visible ? 1 : 0,
              transform: en3D
                ? `translateZ(${abiertas ? (i - (n - 1) / 2) * PASO_Z : 0}px) rotate(${c.rot}deg)`
                : `rotate(${c.rot}deg)`,
              transition: gesto.current?.id === c.id ? 'opacity 220ms ease-out' : transicion,
              outline: abiertas
                ? '1px solid rgba(255,255,255,0.45)'
                : c.id === resaltada && !c.esBase
                  ? '1.5px solid rgba(37,99,245,0.9)'
                  : '1.5px solid transparent',
              outlineOffset: -1,
              boxShadow: abiertas ? '0 18px 40px -18px rgba(0,0,0,0.55)' : 'none',
              cursor: interactivo && !c.esBase ? 'move' : 'default',
              pointerEvents: c.visible ? 'auto' : 'none',
              touchAction: 'none',
            }}
            onPointerDown={(e) => {
              if (!interactivo) return;
              if (c.esBase) return; // el fondo deselecciona (lo toma el contenedor)
              onSeleccionar(c.id);
              empezar(e, { tipo: 'mover', id: c.id, x0: e.clientX, y0: e.clientY, capa: c });
            }}
            onPointerMove={seguir}
            onPointerUp={soltar}
            onPointerCancel={soltar}
          >
            <img
              src={c.src}
              alt=""
              draggable={false}
              className="pointer-events-none h-full w-full transition-[filter] duration-300"
              style={{ filter: c.id === capaEnProceso ? 'brightness(0.8) saturate(0.85)' : undefined }}
            />
            {c.id === capaEnProceso && (
              <span className="pointer-events-none absolute inset-0 overflow-hidden">
                <span className="brillo-de-edicion" />
              </span>
            )}
          </div>
        ))}

        {/* El marco de la elegida: esquinas para escalar y el punto de arriba para girar. */}
        {elegida && !abiertas && interactivo && (
          <div
            className="pointer-events-none absolute animate-fade-in"
            style={{
              ...cajaCss(elegida),
              zIndex: n + 1,
              transform: `rotate(${elegida.rot}deg)`,
              outline: '1.5px solid #2563F5',
              outlineOffset: 0,
            }}
          >
            {(
              [
                ['-left-[7px] -top-[7px]', 'nwse-resize'],
                ['-right-[7px] -top-[7px]', 'nesw-resize'],
                ['-left-[7px] -bottom-[7px]', 'nesw-resize'],
                ['-right-[7px] -bottom-[7px]', 'nwse-resize'],
              ] as const
            ).map(([pos, cursor]) => (
              <span
                key={pos}
                className={`pointer-events-auto absolute h-[14px] w-[14px] rounded-full border-2 border-[#2563F5] bg-white shadow-sm transition-transform duration-150 hover:scale-125 ${pos}`}
                style={{ cursor, touchAction: 'none' }}
                onPointerDown={(e) => {
                  const { cx, cy } = centro(elegida);
                  empezar(e, { tipo: 'escalar', id: elegida.id, d0: Math.hypot(e.clientX - cx, e.clientY - cy), cx, cy, capa: elegida });
                }}
                onPointerMove={seguir}
                onPointerUp={soltar}
                onPointerCancel={soltar}
              />
            ))}
            <span className="absolute left-1/2 top-[-26px] h-[20px] w-px -translate-x-1/2 bg-[#2563F5]" />
            <span
              className="pointer-events-auto absolute left-1/2 top-[-34px] h-[16px] w-[16px] -translate-x-1/2 rounded-full border-2 border-[#2563F5] bg-white shadow-sm transition-transform duration-150 hover:scale-125"
              style={{ cursor: 'grab', touchAction: 'none' }}
              title="Girar"
              onPointerDown={(e) => {
                const { cx, cy } = centro(elegida);
                empezar(e, { tipo: 'rotar', id: elegida.id, a0: (Math.atan2(e.clientY - cy, e.clientX - cx) * 180) / Math.PI, cx, cy, capa: elegida });
              }}
              onPointerMove={seguir}
              onPointerUp={soltar}
              onPointerCancel={soltar}
            />
          </div>
        )}
        {elegida && bajoLaCapa && !abiertas && !moviendo && (interactivo || capaEnProceso === elegida.id) && (
          <div
            className="absolute"
            style={{ ...anclaDebajo(elegida), zIndex: n + 3, transform: 'translateX(-50%)', pointerEvents: 'auto' }}
            // Tocar la barra no es tocar el fondo (que suelta la capa).
            onPointerDown={(e) => e.stopPropagation()}
          >
            {bajoLaCapa(elegida)}
          </div>
        )}
        {ocupado && (
          <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-lg" style={{ zIndex: n + 2 }}>
            <span className="brillo-de-edicion" />
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

// ── El nombre de una acción al pasar el mouse ─────────────────────────────────
// Los íconos de cada fila (guardar, editar con IA, ocultar) dicen qué hacen
// (ADR-2026-10-20-las-acciones-de-la-capa-dicen-su-nombre). Va por portal y con posición
// fija: la lista vive en una caja con `overflow-hidden` que lo cortaría en la primera fila.

/** Lo mínimo que el rótulo queda separado del borde de la pantalla. */
const MARGEN_DEL_ROTULO = 8;

const ConRotulo: React.FC<{ rotulo: string; children: ReactNode }> = ({ rotulo, children }) => {
  const [ancla, setAncla] = useState<{ x: number; y: number } | null>(null);
  // Centrado sobre el botón, pero adentro de la pantalla: el panel está pegado al borde
  // derecho y "Guardar en Elementos de marca" se salía. Se mide antes de pintar.
  const rotuloRef = useRef<HTMLSpanElement | null>(null);
  // La posición se calcula con las medidas, sin `translate`: `animate-fade-in` anima
  // `transform` y queda aplicado al terminar, así que pisaba el `-translate-y-full` y el
  // rótulo bajaba sobre el botón.
  const [posicion, setPosicion] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const el = rotuloRef.current;
    if (!ancla || !el) return setPosicion(null);
    const ancho = el.offsetWidth;
    const max = window.innerWidth - ancho - MARGEN_DEL_ROTULO;
    setPosicion({
      left: Math.max(MARGEN_DEL_ROTULO, Math.min(ancla.x - ancho / 2, max)),
      top: Math.max(MARGEN_DEL_ROTULO, ancla.y - el.offsetHeight - 6),
    });
  }, [ancla, rotulo]);
  const mostrar = (el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    setAncla({ x: r.left + r.width / 2, y: r.top });
  };
  return (
    <span
      className="inline-flex shrink-0"
      onPointerEnter={(e) => e.pointerType === 'mouse' && mostrar(e.currentTarget)}
      onPointerLeave={() => setAncla(null)}
      onPointerDown={() => setAncla(null)}
      onFocus={(e) => mostrar(e.currentTarget)}
      onBlur={() => setAncla(null)}
    >
      {children}
      {ancla &&
        createPortal(
          <span
            ref={rotuloRef}
            role="tooltip"
            className="pointer-events-none fixed z-[300] whitespace-nowrap rounded-md bg-[#1C1D22] px-2 py-1 text-[12px] font-medium text-white shadow-[0_6px_18px_-6px_rgba(0,0,0,0.6)] ring-1 ring-white/10 animate-fade-in"
            style={{ left: posicion?.left ?? ancla.x, top: posicion?.top ?? ancla.y, visibility: posicion ? 'visible' : 'hidden' }}
          >
            {rotulo}
          </span>,
          document.body,
        )}
    </span>
  );
};

// ── La lista de capas ───────────────────────────────────────────────────────────

const ListaDeCapas: React.FC<{
  capas: CapaEnLienzo[];
  seleccion: string | null;
  disabled: boolean;
  onSeleccionar: (id: string) => void;
  onResaltar: (id: string | null) => void;
  onVisible: (id: string) => void;
  onReordenar: (desde: number, hasta: number) => void;
  /** Abre la barra de "Editar capa" para esa capa. */
  onEditar: (id: string) => void;
  /** Guarda la capa como elemento de marca. Sin él, no hay botón. */
  onGuardarComoElemento?: (id: string) => void;
  /** Qué capas se están guardando o ya se guardaron como elemento. */
  guardadoComoElemento?: Record<string, 'guardando' | 'listo'>;
}> = ({ capas, seleccion, disabled, onSeleccionar, onResaltar, onVisible, onReordenar, onEditar, onGuardarComoElemento, guardadoComoElemento = {} }) => {
  // Adelante arriba, como en cualquier editor: la lista es el arreglo al revés.
  const lista = useMemo(() => [...capas].reverse(), [capas]);
  const total = lista.length;
  const [arrastre, setArrastre] = useState<{ desde: number; dy: number; y0: number; movio: boolean } | null>(null);
  // La base (última de la lista) no se mueve, y nada pasa debajo de ella.
  const ultimaMovible = lista[total - 1]?.esBase ? total - 2 : total - 1;
  const hasta = arrastre ? Math.min(ultimaMovible, Math.max(0, Math.round(arrastre.desde + arrastre.dy / PASO_FILA))) : null;

  const corrimiento = (k: number) => {
    if (!arrastre || hasta === null || k === arrastre.desde) return 0;
    if (arrastre.desde < hasta && k > arrastre.desde && k <= hasta) return -PASO_FILA;
    if (arrastre.desde > hasta && k < arrastre.desde && k >= hasta) return PASO_FILA;
    return 0;
  };

  return (
    <div role="list" aria-label="Capas" className="relative flex flex-col gap-1 p-1.5">
      {lista.map((c, k) => {
        const arrastrando = arrastre?.desde === k && arrastre.movio;
        const dy = arrastre?.desde === k ? arrastre.dy : corrimiento(k);
        const activa = c.id === seleccion;
        return (
          <div
            key={c.id}
            role="listitem"
            className="capa-entra"
            style={{ ['--i' as string]: k } as CSSProperties}
          >
            <div
              className={`group flex items-center gap-2.5 rounded-[10px] px-2 transition-colors ${
                activa ? 'bg-white/[0.12]' : 'hover:bg-white/[0.06]'
              } ${arrastrando ? 'relative z-10 bg-white/[0.14] shadow-[0_10px_24px_-12px_rgba(0,0,0,0.7)]' : ''}`}
              style={{
                height: ALTO_FILA,
                transform: `translateY(${dy}px) ${arrastrando ? 'scale(1.02)' : ''}`,
                transition: arrastre?.desde === k ? 'background-color 150ms, box-shadow 150ms' : `transform 220ms ${CURVA}, background-color 150ms`,
                cursor: c.esBase || disabled ? 'default' : arrastrando ? 'grabbing' : 'grab',
                touchAction: 'none',
              }}
              onMouseEnter={() => onResaltar(c.id)}
              onMouseLeave={() => onResaltar(null)}
              onPointerDown={(e) => {
                if (disabled || (e.target as HTMLElement).closest('button')) return;
                if (c.esBase) return onSeleccionar(c.id);
                e.currentTarget.setPointerCapture(e.pointerId);
                setArrastre({ desde: k, dy: 0, y0: e.clientY, movio: false });
              }}
              onPointerMove={(e) => {
                if (!arrastre || arrastre.desde !== k) return;
                const dyNuevo = e.clientY - arrastre.y0;
                setArrastre({ ...arrastre, dy: dyNuevo, movio: arrastre.movio || Math.abs(dyNuevo) > 3 });
              }}
              onPointerUp={() => {
                if (!arrastre || arrastre.desde !== k) return;
                if (!arrastre.movio) onSeleccionar(c.id);
                else if (hasta !== null && hasta !== k) onReordenar(total - 1 - k, total - 1 - hasta);
                setArrastre(null);
              }}
              onPointerCancel={() => setArrastre(null)}
            >
              {c.esBase ? (
                <Lock className="h-3.5 w-3.5 shrink-0 text-white/30" aria-hidden="true" />
              ) : (
                <GripVertical className="h-4 w-4 shrink-0 text-white/30 transition-colors group-hover:text-white/60" aria-hidden="true" />
              )}
              <span className="relative grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-md" style={TABLERO}>
                <img src={c.src} alt="" draggable={false} className={`max-h-full max-w-full object-contain transition-opacity duration-200 ${c.visible ? '' : 'opacity-30'}`} />
              </span>
              <span className={`min-w-0 flex-1 truncate text-[13.5px] font-medium transition-colors ${c.visible ? 'text-white/90' : 'text-white/40'}`}>
                {c.nombre}
              </span>
              {/* Guardar la capa como elemento de marca: el listón, siempre a la vista
                  (ADR-2026-10-20-una-capa-se-guarda-como-elemento-de-marca,
                  ADR-2026-10-20-las-acciones-de-la-capa-dicen-su-nombre). */}
              {!c.esBase && onGuardarComoElemento && (
                <ConRotulo
                  rotulo={
                    guardadoComoElemento[c.id] === 'listo'
                      ? 'Guardado en Elementos de marca'
                      : guardadoComoElemento[c.id] === 'guardando'
                        ? 'Guardando…'
                        : 'Guardar en Elementos de marca'
                  }
                >
                  <button
                    type="button"
                    disabled={disabled || !!guardadoComoElemento[c.id]}
                    onClick={() => onGuardarComoElemento(c.id)}
                    aria-label={`Guardar ${c.nombre} en Elementos de marca`}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-white/55 transition-colors hover:bg-white/[0.1] hover:text-white disabled:hover:bg-transparent"
                  >
                    {guardadoComoElemento[c.id] === 'guardando' ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : guardadoComoElemento[c.id] === 'listo' ? (
                      <BookmarkCheck className="h-4 w-4 text-emerald-300" aria-hidden="true" />
                    ) : (
                      <Bookmark className="h-4 w-4" aria-hidden="true" />
                    )}
                  </button>
                </ConRotulo>
              )}
              {!c.esBase && (
                <ConRotulo rotulo="Editar con IA">
                  <button
                    type="button"
                    disabled={disabled || !c.visible}
                    onClick={() => onEditar(c.id)}
                    aria-label={`Editar ${c.nombre}`}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-white/55 transition-colors hover:bg-white/[0.1] hover:text-white disabled:opacity-30"
                  >
                    <Sparkles className="h-4 w-4" aria-hidden="true" />
                  </button>
                </ConRotulo>
              )}
              <ConRotulo rotulo={c.visible ? 'Ocultar' : 'Mostrar'}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onVisible(c.id)}
                  aria-label={c.visible ? `Ocultar ${c.nombre}` : `Mostrar ${c.nombre}`}
                  aria-pressed={!c.visible}
                  className={`grid h-8 w-8 shrink-0 place-items-center rounded-full transition-colors hover:bg-white/[0.1] hover:text-white disabled:opacity-40 ${
                    c.visible ? 'text-white/55' : 'text-white/80'
                  }`}
                >
                  {c.visible ? <Eye className="h-4 w-4" aria-hidden="true" /> : <EyeOff className="h-4 w-4" aria-hidden="true" />}
                </button>
              </ConRotulo>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Editar capa: la pastilla que se abre en barra de prompt ────────────────────
// ADR-2026-10-07-editar-una-capa-con-sunburst. Como la barra de Editar del visor, pero sin
// adjuntos: sólo el texto y "Editar" con su costo. Sunburst `medium`, fondo transparente; el
// backend suma al pie que no toque nada más y que lo transparente siga transparente.

const ALTO_RENGLON = 20;
const MAX_RENGLONES = 3;

const BarraDeCapa: React.FC<{
  nombre: string;
  abierta: boolean;
  enviando: boolean;
  onAbrir: () => void;
  onCerrar: () => void;
  onEnviar: (texto: string) => void;
  /** Hay una edición para deshacer (vuelve al PNG anterior, un paso por toque). */
  puedeRevertir: boolean;
  onRevertir: () => void;
}> = ({ nombre, abierta, enviando, onAbrir, onCerrar, onEnviar, puedeRevertir, onRevertir }) => {
  const [texto, setTexto] = useState('');
  const [altoTexto, setAltoTexto] = useState(ALTO_RENGLON);
  const ref = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (abierta) window.setTimeout(() => ref.current?.focus(), 180);
  }, [abierta]);
  const ajustar = (el: HTMLTextAreaElement) => {
    el.style.height = '0px';
    const alto = Math.min(ALTO_RENGLON * MAX_RENGLONES, Math.max(ALTO_RENGLON, el.scrollHeight));
    el.style.height = `${alto}px`;
    setAltoTexto(alto);
  };
  const enviar = () => {
    const t = texto.trim();
    if (t && !enviando) onEnviar(t);
  };
  const mostrarRevertir = puedeRevertir && !abierta && !enviando;
  return (
    <div className="flex items-center gap-2">
    <div
      // Cerrada, negro casi opaco; abierta, todavía más, para que el texto se lea sobre
      // cualquier imagen (ADR-2026-10-07-la-capa-editada-no-toca-lo-demas,
      // ADR-2026-10-07-revertir-la-edicion-de-una-capa).
      className={`relative overflow-hidden border border-white/[0.1] shadow-[0_14px_32px_-14px_rgba(0,0,0,0.7)] backdrop-blur-xl ${
        abierta ? 'bg-[rgba(10,12,18,0.94)]' : 'bg-[rgba(10,12,18,0.82)]'
      }`}
      style={{
        width: abierta ? 400 : 132,
        height: abierta ? altoTexto + 64 : 36,
        borderRadius: abierta ? 24 : 18,
        transition: `width 320ms ${CURVA}, height 240ms ${CURVA}, border-radius 320ms ${CURVA}, background-color 200ms ease-out`,
      }}
    >
      {/* Cerrada: la pastilla. */}
      <button
        type="button"
        onClick={onAbrir}
        aria-hidden={abierta}
        tabIndex={abierta ? -1 : 0}
        className="absolute inset-0 inline-flex items-center justify-center gap-1.5 whitespace-nowrap text-[13px] font-semibold text-white transition-[opacity,background-color] duration-200 hover:bg-white/[0.08]"
        style={{ opacity: abierta ? 0 : 1, pointerEvents: abierta ? 'none' : 'auto' }}
      >
        <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
        Editar capa
      </button>
      {/* Abierta: el texto y "Editar". */}
      <form
        aria-hidden={!abierta}
        onSubmit={(e) => {
          e.preventDefault();
          enviar();
        }}
        className="absolute inset-x-0 top-0 flex flex-col gap-2 p-2.5 pt-3"
        style={{
          opacity: abierta ? 1 : 0,
          transform: abierta ? 'none' : 'translateY(4px)',
          pointerEvents: abierta ? 'auto' : 'none',
          transition: `opacity 180ms ease-out ${abierta ? '120ms' : '0ms'}, transform 260ms ${CURVA} ${abierta ? '100ms' : '0ms'}`,
        }}
      >
        <textarea
          ref={ref}
          rows={1}
          value={texto}
          disabled={enviando || !abierta}
          onChange={(e) => {
            setTexto(e.target.value.slice(0, 1000));
            ajustar(e.currentTarget);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              if (!enviando) onCerrar();
            } else if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              enviar();
            }
          }}
          placeholder={`¿Qué le cambiás a ${nombre.toLowerCase()}?`}
          aria-label={`Qué cambiarle a ${nombre}`}
          className="w-full resize-none bg-transparent px-2.5 text-[14px] leading-5 text-white outline-none placeholder:text-white/40 disabled:opacity-60"
          style={{ height: altoTexto }}
        />
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCerrar}
            disabled={enviando}
            className="h-9 rounded-full px-3 text-[13px] font-medium text-white/65 transition-colors hover:bg-white/[0.08] hover:text-white disabled:opacity-40"
          >
            Cancelar
          </button>
          <BotonConCreditos type="submit" creditos={CREDITOS_EDITAR_CAPA} ocupado={enviando} disabled={!texto.trim()}>
            {enviando ? 'Editando…' : 'Editar'}
          </BotonConCreditos>
        </div>
      </form>
    </div>
    {/* Revertir: al lado de la pastilla, sólo si la capa tiene una edición para deshacer. */}
    <button
      type="button"
      onClick={onRevertir}
      aria-hidden={!mostrarRevertir}
      tabIndex={mostrarRevertir ? 0 : -1}
      aria-label={`Revertir la edición de ${nombre}`}
      title="Revertir la edición"
      className="grid h-9 shrink-0 place-items-center overflow-hidden rounded-full border border-white/[0.1] bg-[rgba(10,12,18,0.82)] text-white/80 shadow-[0_14px_32px_-14px_rgba(0,0,0,0.7)] backdrop-blur-xl transition-colors hover:bg-[rgba(30,33,42,0.9)] hover:text-white"
      style={{
        width: mostrarRevertir ? 36 : 0,
        marginLeft: mostrarRevertir ? 0 : -8,
        opacity: mostrarRevertir ? 1 : 0,
        borderWidth: mostrarRevertir ? 1 : 0,
        pointerEvents: mostrarRevertir ? 'auto' : 'none',
        transition: `width 260ms ${CURVA}, margin 260ms ${CURVA}, opacity 200ms ease-out, background-color 150ms, color 150ms`,
      }}
    >
      <RotateCcw className="h-4 w-4" aria-hidden="true" />
    </button>
    </div>
  );
};

// ── El interruptor ──────────────────────────────────────────────────────────────

const Interruptor: React.FC<{ activo: boolean; onCambiar: (v: boolean) => void; disabled?: boolean; etiqueta: string }> = ({
  activo,
  onCambiar,
  disabled,
  etiqueta,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={activo}
    aria-label={etiqueta}
    disabled={disabled}
    onClick={() => onCambiar(!activo)}
    className={`relative h-[26px] w-[44px] shrink-0 rounded-full transition-colors duration-200 disabled:opacity-40 ${activo ? 'bg-[#1644EB]' : 'bg-white/[0.16]'}`}
  >
    <span
      className="absolute left-[3px] top-[3px] h-5 w-5 rounded-full bg-white shadow"
      style={{ transform: activo ? 'translateX(18px)' : 'none', transition: `transform 220ms ${CURVA}` }}
    />
  </button>
);

// ── El modo ─────────────────────────────────────────────────────────────────────

export const ModoCapas: React.FC<{
  /** El panel está a la vista (si el visor sale de editar, el lienzo se va con él). */
  visible: boolean;
  imgRef: RefObject<HTMLImageElement>;
  ocupado: boolean;
  /** Separa con la instrucción (o sin ella) y entrega el resultado a `alTerminar`. */
  onSeparar: (instruccion: string | undefined, alTerminar: (s: Separacion) => void) => void;
  /** Guarda lo que se ve como versión nueva (la composición la arma quien guarda). */
  onGuardar: (datos: { capas: CapaEnLienzo[]; ancho: number; alto: number; formato?: string }) => void;
  /** Edita una capa con IA y entrega la capa nueva (o `null` si falló) a `alTerminar`. */
  onEditarCapa: (capa: CapaEnLienzo, instruccion: string, alTerminar: (nueva: CapaEnLienzo | null) => void) => void;
  /** Hay capas sin guardar: el panel no deja volver a la lista sin pasar por "Cancelar". */
  onEditando: (editando: boolean) => void;
  onCancelar: () => void;
  /** Guarda el PNG de una capa en Elementos de marca y dice en qué carpeta quedó. */
  onGuardarComoElemento?: (capa: CapaEnLienzo) => Promise<{ carpeta: string }>;
}> = ({ visible, imgRef, ocupado, onSeparar, onGuardar, onEditarCapa, onEditando, onCancelar, onGuardarComoElemento }) => {
  const [conInstruccion, setConInstruccion] = useState(false);
  const [instruccion, setInstruccion] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const [separacion, setSeparacion] = useState<Separacion | null>(null);
  const [capas, setCapas] = useState<CapaEnLienzo[]>([]);
  const [seleccion, setSeleccion] = useState<string | null>(null);
  /** "Guardar en Elementos de marca", por capa, y lo que se le cuenta a la persona. */
  const [guardadoComoElemento, setGuardadoComoElemento] = useState<Record<string, 'guardando' | 'listo'>>({});
  const [avisoDeElemento, setAvisoDeElemento] = useState<{ error: boolean; texto: string } | null>(null);
  /** Una capa que cambió (editada o revertida) es otra imagen: se puede volver a guardar. */
  const olvidarGuardado = (id: string) =>
    setGuardadoComoElemento((g) => (g[id] === 'listo' ? Object.fromEntries(Object.entries(g).filter(([k]) => k !== id)) : g));
  const [resaltada, setResaltada] = useState<string | null>(null);
  const [abiertas, setAbiertas] = useState(false);
  const [interactivo, setInteractivo] = useState(false);
  const [confirmarCancelar, setConfirmarCancelar] = useState(false);
  /** La barra de "Editar capa" está abierta (para la elegida). */
  const [barraAbierta, setBarraAbierta] = useState(false);
  /** La capa que se está editando con IA. */
  const [editandoId, setEditandoId] = useState<string | null>(null);
  /** Por capa, las ediciones que se pueden deshacer (la última al final). */
  const [historial, setHistorial] = useState<Record<string, EdicionDeCapa[]>>({});
  const enLienzo = !!separacion && visible;
  const rect = useCalceSobre(imgRef, enLienzo);

  // Revisión de la instrucción: una pausa después de escribir; la anterior se aborta.
  useEffect(() => {
    const texto = instruccion.trim();
    if (!conInstruccion || texto.length < 2) {
      setAviso(null);
      return;
    }
    const ctrl = new AbortController();
    const t = window.setTimeout(() => {
      void revisarInstruccionDeCapas(texto, ctrl.signal).then((a) => !ctrl.signal.aborted && setAviso(a));
    }, ESPERA_REVISION_MS);
    return () => {
      window.clearTimeout(t);
      ctrl.abort();
    };
  }, [instruccion, conInstruccion]);

  // Con capas en el lienzo, la imagen del visor se esconde (las capas la reemplazan).
  // `visibility` y no `opacity`: la opacidad la maneja el visor mientras carga.
  useEffect(() => {
    if (!enLienzo) return;
    const img = imgRef.current;
    if (img) img.style.visibility = 'hidden';
    return () => {
      if (img) img.style.visibility = '';
    };
  }, [enLienzo, imgRef]);

  useEffect(() => {
    onEditando(!!separacion);
    return () => onEditando(false);
  }, [separacion, onEditando]);

  // La apertura: se abren, quedan un instante y vuelven a su lugar; recién ahí se editan.
  const animarApertura = useCallback(() => {
    if (prefiereMenosMovimiento()) {
      setInteractivo(true);
      return;
    }
    const timers: number[] = [];
    timers.push(window.setTimeout(() => setAbiertas(true), 60));
    timers.push(window.setTimeout(() => setAbiertas(false), 60 + ABRIR_MS + ABIERTA_MS));
    timers.push(window.setTimeout(() => setInteractivo(true), 60 + 2 * ABRIR_MS + ABIERTA_MS));
    return () => timers.forEach(window.clearTimeout);
  }, []);
  const [disparo, setDisparo] = useState(0);
  useEffect(() => (disparo > 0 ? animarApertura() : undefined), [disparo, animarApertura]);

  const separar = () => {
    onSeparar(conInstruccion ? instruccion.trim() || undefined : undefined, (s) => {
      setSeparacion(s);
      setCapas(s.capas);
      setHistorial({});
      setGuardadoComoElemento({});
      setAvisoDeElemento(null);
      setSeleccion(null);
      setInteractivo(false);
      setDisparo((d) => d + 1);
    });
  };

  const cambiarCapa = useCallback((c: CapaEnLienzo) => setCapas((cs) => cs.map((x) => (x.id === c.id ? c : x))), []);
  const alternarVisible = (id: string) => setCapas((cs) => cs.map((x) => (x.id === id ? { ...x, visible: !x.visible } : x)));

  const guardar = () => {
    if (!separacion) return;
    setSeleccion(null);
    onGuardar({ capas, ancho: separacion.ancho, alto: separacion.alto, formato: formatoDeMedidas(separacion.ancho, separacion.alto) });
  };

  const descartar = () => {
    setSeparacion(null);
    setCapas([]);
    setGuardadoComoElemento({});
    setAvisoDeElemento(null);
    setConfirmarCancelar(false);
    onCancelar();
  };

  // Escape suelta la elegida y NADA más: el visor usa Escape para salir de editar, y eso
  // tiraría las capas sin guardar. En captura, para llegar antes que el visor.
  useEffect(() => {
    if (!enLienzo) return;
    const tecla = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      if (editandoId) return;
      // Primero cierra la barra de editar; otro Escape suelta la capa.
      if (barraAbierta) return setBarraAbierta(false);
      setSeleccion(null);
      setConfirmarCancelar(false);
    };
    window.addEventListener('keydown', tecla, true);
    return () => window.removeEventListener('keydown', tecla, true);
  }, [enLienzo, barraAbierta, editandoId]);

  // Elegir otra capa (o ninguna) cierra la barra, salvo que se esté editando.
  const elegir = (id: string | null) => {
    if (editandoId) return;
    if (id !== seleccion) setBarraAbierta(false);
    setSeleccion(id);
  };

  const editarLaCapa = (c: CapaEnLienzo, texto: string) => {
    setEditandoId(c.id);
    onEditarCapa(c, texto, (nueva) => {
      setEditandoId(null);
      if (!nueva) return;
      // Se reemplaza la capa por id: si mientras tanto se movió, el centro nuevo es el de ahora.
      const llegada = { ...nueva, cx: c.cx, cy: c.cy, rot: c.rot, visible: c.visible };
      setCapas((cs) => cs.map((x) => (x.id === nueva.id ? { ...llegada, cx: x.cx, cy: x.cy, rot: x.rot, visible: x.visible } : x)));
      setHistorial((h) => ({ ...h, [c.id]: [...(h[c.id] ?? []), pasoDeHistorial(c, llegada)] }));
      olvidarGuardado(c.id);
      setBarraAbierta(false);
    });
  };

  // Revertir: un paso por toque, hasta volver a la capa como salió de la separación.
  const revertirLaCapa = (id: string) => {
    const pasos = historial[id];
    const paso = pasos?.[pasos.length - 1];
    if (!paso || editandoId) return;
    setCapas((cs) => cs.map((x) => (x.id === id ? revertirEdicion(x, paso) : x)));
    setHistorial((h) => ({ ...h, [id]: pasos.slice(0, -1) }));
    olvidarGuardado(id);
  };

  const guardarComoElemento = async (id: string) => {
    const capa = capas.find((c) => c.id === id);
    if (!capa || !onGuardarComoElemento || guardadoComoElemento[id]) return;
    setGuardadoComoElemento((g) => ({ ...g, [id]: 'guardando' }));
    setAvisoDeElemento(null);
    try {
      const { carpeta } = await onGuardarComoElemento(capa);
      setGuardadoComoElemento((g) => ({ ...g, [id]: 'listo' }));
      setAvisoDeElemento({ error: false, texto: `«${capa.nombre}» quedó en Elementos de marca → ${carpeta}.` });
    } catch (e) {
      setGuardadoComoElemento((g) => Object.fromEntries(Object.entries(g).filter(([k]) => k !== id)));
      setAvisoDeElemento({
        error: true,
        texto: e instanceof Error && e.message ? e.message : 'No pudimos guardar la capa. Probá de nuevo.',
      });
    }
  };

  if (separacion) {
    const nadaVisible = capas.every((c) => !c.visible);
    const igual = sinCambios(capas, separacion.capas);
    return (
      <>
        {rect && enLienzo && (
          <LienzoDeCapas
            rect={rect}
            capas={capas}
            abiertas={abiertas}
            en3D={!interactivo}
            interactivo={interactivo && !ocupado}
            ocupado={ocupado && !editandoId}
            seleccion={seleccion}
            resaltada={resaltada}
            onSeleccionar={elegir}
            onCambiar={cambiarCapa}
            capaEnProceso={editandoId}
            bajoLaCapa={(c) => (
              <BarraDeCapa
                key={c.id}
                nombre={c.nombre}
                abierta={barraAbierta}
                enviando={editandoId === c.id}
                onAbrir={() => setBarraAbierta(true)}
                onCerrar={() => setBarraAbierta(false)}
                onEnviar={(texto) => editarLaCapa(c, texto)}
                puedeRevertir={(historial[c.id]?.length ?? 0) > 0}
                onRevertir={() => revertirLaCapa(c.id)}
              />
            )}
          />
        )}
        <Cuerpo>
          <Seccion titulo={`${capas.length - 1} ${capas.length - 1 === 1 ? 'capa' : 'capas'} y el fondo`}>
            <ListaDeCapas
              capas={capas}
              seleccion={seleccion}
              disabled={ocupado || !interactivo}
              onSeleccionar={(id) => elegir(id)}
              onResaltar={setResaltada}
              onVisible={alternarVisible}
              onEditar={(id) => {
                if (editandoId) return;
                setSeleccion(id);
                setBarraAbierta(true);
              }}
              onReordenar={(desde, hasta) => setCapas((cs) => reordenarCapas(cs, desde, hasta))}
              onGuardarComoElemento={onGuardarComoElemento ? (id) => void guardarComoElemento(id) : undefined}
              guardadoComoElemento={guardadoComoElemento}
            />
          </Seccion>
          {avisoDeElemento && (
            <p
              key={avisoDeElemento.texto}
              role="status"
              className={`text-[12.5px] leading-snug animate-fade-in ${avisoDeElemento.error ? 'text-red-300' : 'text-emerald-200/90'}`}
            >
              {avisoDeElemento.texto}
            </p>
          )}
          <p className="text-[12.5px] leading-snug text-white/45">
            Arrastrá una capa sobre la imagen para moverla; las esquinas cambian el tamaño y el punto de arriba la gira.
            En la lista, arrastralas para cambiar el orden y tocá el ojo para ocultarlas. Con «Editar capa» le cambiás sólo
            ese elemento con IA, y con el listón lo guardás en tus Elementos de marca.
          </p>
        </Cuerpo>
        <Pie>
          {confirmarCancelar ? (
            <div className="flex w-full items-center gap-2 animate-fade-in">
              <p className="mr-auto text-[12.5px] leading-snug text-white/65">¿Descartar las capas? El crédito ya se usó.</p>
              <button
                type="button"
                onClick={() => setConfirmarCancelar(false)}
                className="h-9 rounded-full px-3 text-[13px] font-medium text-white/70 transition-colors hover:bg-white/[0.08] hover:text-white"
              >
                Seguir
              </button>
              <button
                type="button"
                onClick={descartar}
                className="h-9 rounded-full bg-white/[0.12] px-4 text-[13px] font-semibold text-white transition-colors hover:bg-white/[0.2]"
              >
                Descartar
              </button>
            </div>
          ) : (
            <>
              <button
                type="button"
                disabled={ocupado}
                onClick={() => setConfirmarCancelar(true)}
                className="mr-auto inline-flex h-10 items-center rounded-full px-4 text-[13.5px] font-medium text-white/70 transition-colors hover:bg-white/[0.08] hover:text-white disabled:opacity-40"
              >
                Cancelar
              </button>
              <BotonConCreditos ocupado={ocupado} disabled={!interactivo || nadaVisible || igual} onClick={guardar}>
                {ocupado ? 'Guardando…' : 'Guardar versión'}
              </BotonConCreditos>
            </>
          )}
        </Pie>
      </>
    );
  }

  const faltaTexto = conInstruccion && !instruccion.trim();
  return (
    <>
      <Cuerpo>
        <IlustracionDeCapas />
        <p className="text-[12.5px] leading-snug text-white/55">
          Cada elemento de la imagen queda en su propia capa: después los movés, los girás, les cambiás el tamaño, los ordenás o los ocultás.
        </p>
        <Seccion titulo="Qué separar">
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="min-w-0">
              <span className="block text-[14px] text-white/90">Elegir qué separar</span>
              <span className="mt-0.5 block text-[12px] leading-snug text-white/45">
                {conInstruccion ? 'Nombrá los elementos que querés sueltos.' : 'Si no, separamos los textos y gráficos principales.'}
              </span>
            </span>
            <Interruptor activo={conInstruccion} onCambiar={setConInstruccion} disabled={ocupado} etiqueta="Elegir qué separar" />
          </div>
          <div
            className="grid transition-[grid-template-rows,opacity] duration-300"
            style={{ gridTemplateRows: conInstruccion ? '1fr' : '0fr', opacity: conInstruccion ? 1 : 0, transitionTimingFunction: CURVA }}
          >
            <div className="min-h-0 overflow-hidden">
              <div className="border-t border-white/[0.07] px-3 pb-3 pt-2.5">
                <textarea
                  value={instruccion}
                  onChange={(e) => setInstruccion(e.target.value.slice(0, MAX_LARGO_INSTRUCCION_CAPAS))}
                  disabled={ocupado || !conInstruccion}
                  rows={3}
                  placeholder="Ej.: el producto, el título y el logo"
                  aria-label="Qué separar"
                  className="w-full resize-none rounded-[10px] bg-white/[0.06] px-3 py-2 text-[13.5px] leading-snug text-white outline-none ring-white/20 placeholder:text-white/35 focus:ring-1"
                />
              </div>
            </div>
          </div>
        </Seccion>
        {conInstruccion && aviso && (
          <div key={aviso} className="flex gap-2.5 rounded-[13px] border border-amber-300/20 bg-amber-300/10 px-3.5 py-2.5 animate-fade-in" role="status">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-200" aria-hidden="true" />
            <p className="text-[12.5px] leading-snug text-amber-100/90">{aviso}</p>
          </div>
        )}
      </Cuerpo>
      <Pie>
        <BotonConCreditos creditos={CREDITOS_SEPARAR_EN_CAPAS} ocupado={ocupado} disabled={faltaTexto} onClick={separar}>
          {ocupado ? 'Separando…' : 'Separar en capas'}
        </BotonConCreditos>
      </Pie>
    </>
  );
};

export default ModoCapas;
